/* eslint-disable complexity */
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import RadioButton, { RadioButtonGroup } from 'carbon-react/lib/components/radio-button';
import Textbox from 'carbon-react/lib/components/textbox';

import { ReportTemplateList } from '@futrli-sdk/predict';

import { FutrliTemplateSelect } from './futrli-template-select';
import { OrientationPicker } from './orientation-picker';
import { WorkspaceTemplateSelect } from './workspace-template-select';
import { REPORT_FORM_FIELD_IDS } from '../constants';
import { HelpLinkSection } from '../../help-link-section/help-link-section';
import {
  FooterMode,
  WizardScreen
} from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { WizardFormValidationErrorSummary } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-validation-error-summary/wizard-form-validation-error-summary';
import { CreateReportSidebarMode } from '../create-report-sidebar';
import { WizardFormError } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-error/wizard-form-error';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';

export type CreationMethod = '' | 'scratch' | 'myTemplate' | 'futrliTemplate';

export interface ReportFormScreenProps {
  error?: string;
  validationErrors?: Record<string, { id: string; message: string }>;
  reportName: string;
  creationMethod: CreationMethod;
  orientation: 'portrait' | 'landscape' | 'dashboard';
  selectedTemplateId: string;
  workspaceTemplates: ReportTemplateList[];
  futrliTemplates: ReportTemplateList[];
  onReportNameChange: (value: string) => void;
  onCreationMethodChange: (value: CreationMethod) => void;
  onOrientationChange: (value: 'portrait' | 'landscape') => void;
  onSelectedTemplateIdChange: (value: string) => void;
  onClose: () => void;
  onCreate: () => void;
  onNavigateToCreateTemplate: () => void;
  onBlur?: () => void;
  mode?: CreateReportSidebarMode;
}

export const ReportFormScreen = ({
  error,
  validationErrors = {},
  reportName,
  creationMethod,
  orientation,
  selectedTemplateId,
  workspaceTemplates,
  futrliTemplates,
  onReportNameChange,
  onCreationMethodChange,
  onOrientationChange,
  onSelectedTemplateIdChange,
  onClose,
  onCreate,
  onNavigateToCreateTemplate,
  onBlur,
  mode = 'report'
}: ReportFormScreenProps) => {
  const intl = useIntl();
  const isDashboard = mode === 'dashboard';

  const validCreationMethods = ['scratch', 'myTemplate', 'futrliTemplate'];

  const handleCreationMethodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as CreationMethod;
    if (!validCreationMethods.includes(value)) {
      return;
    }
    onCreationMethodChange(value);
    onSelectedTemplateIdChange('');
  };

  return (
    <WizardScreen
      footerMode={FooterMode.CREATE}
      onCancel={onClose}
      onCreate={onCreate}
      createText={<FormattedMessage id="common.create" defaultMessage="Create" />}
    >
      <>
        {error && <WizardFormError message={error} mb={3} />}

        <WizardFormValidationErrorSummary errors={validationErrors} mb={3} />

        <WizardRequiredFieldInfo />

        <Box mb={3}>
          <Textbox
            label={
              isDashboard
                ? intl.formatMessage({ id: 'reporting.dashboard_name', defaultMessage: 'Dashboard name' })
                : intl.formatMessage({ id: 'reporting.report_name', defaultMessage: 'Report name' })
            }
            name={REPORT_FORM_FIELD_IDS.REPORT_NAME}
            id={REPORT_FORM_FIELD_IDS.REPORT_NAME}
            value={reportName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onReportNameChange(e.target.value)}
            onBlur={onBlur}
            error={validationErrors.name?.message}
            required
          />
        </Box>

        <Box mb={3}>
          <RadioButtonGroup
            name={REPORT_FORM_FIELD_IDS.CREATION_METHOD}
            id={REPORT_FORM_FIELD_IDS.CREATION_METHOD}
            legend={intl.formatMessage({
              id: 'reporting.creation_method',
              defaultMessage: 'Creation method'
            })}
            onChange={handleCreationMethodChange}
            value={creationMethod}
            error={validationErrors.creationMethod?.message}
            required
          >
            <RadioButton
              value="scratch"
              label={intl.formatMessage({
                id: 'reporting.create_from_scratch',
                defaultMessage: 'Create from scratch'
              })}
              checked={creationMethod === 'scratch'}
            />

            {creationMethod === 'scratch' && !isDashboard && (
              <OrientationPicker
                orientation={orientation as 'portrait' | 'landscape'}
                onOrientationChange={onOrientationChange}
              />
            )}

            <RadioButton
              value="myTemplate"
              label={intl.formatMessage({
                id: 'reporting.use_one_of_my_templates',
                defaultMessage: 'Use one of my templates'
              })}
              checked={creationMethod === 'myTemplate'}
            />

            {creationMethod === 'myTemplate' && (
              <div
                style={{
                  marginLeft: '8px',
                  borderLeft: '2px solid #CCD6DB',
                  marginBottom: '12px',
                  paddingLeft: '16px'
                }}
              >
                <WorkspaceTemplateSelect
                  templates={workspaceTemplates}
                  selectedTemplateId={selectedTemplateId}
                  onSelectedTemplateIdChange={onSelectedTemplateIdChange}
                  onNavigateToCreateTemplate={onNavigateToCreateTemplate}
                  error={validationErrors.template?.message}
                  onBlur={onBlur}
                  mode={mode}
                />
              </div>
            )}

            <RadioButton
              value="futrliTemplate"
              label={intl.formatMessage({
                id: 'reporting.use_a_futrli_template',
                defaultMessage: 'Use a Futrli template'
              })}
              checked={creationMethod === 'futrliTemplate'}
            />

            {creationMethod === 'futrliTemplate' && (
              <div
                style={{
                  marginLeft: '8px',
                  borderLeft: '2px solid #CCD6DB',
                  marginBottom: '12px',
                  paddingLeft: '16px'
                }}
              >
                <FutrliTemplateSelect
                  templates={futrliTemplates}
                  selectedTemplateId={selectedTemplateId}
                  onSelectedTemplateIdChange={onSelectedTemplateIdChange}
                  error={validationErrors.template?.message}
                  onBlur={onBlur}
                  mode={mode}
                />
              </div>
            )}
          </RadioButtonGroup>
        </Box>

        <HelpLinkSection
          title={
            isDashboard ? (
              <FormattedMessage
                id="reporting.creating_dashboard_first_time"
                defaultMessage="Creating a dashboard for the first time?"
              />
            ) : (
              <FormattedMessage
                id="reporting.creating_report_first_time"
                defaultMessage="Creating a report for the first time?"
              />
            )
          }
          href={
            isDashboard
              ? 'https://support.futrli.com/en/articles/14597196-create-a-dashboard'
              : 'https://support.futrli.com/en/articles/6627683-create-a-new-report'
          }
        />
      </>
    </WizardScreen>
  );
};
